import { useIndirectTaxFilingV2 } from '@commenda/api/indirect-tax/filings';
import { Alert, Skeleton } from '@commenda/kakashi';
import { SalesTaxFilingTasks } from '@commenda/types/src/sales-tax/types';

import { IndirectTaxRegionHelper } from '../../../indirect-tax/common/helpers/IndirectTaxRegionHelper';
import { useIndirectTaxFilingAccess } from '../../../indirect-tax/common/hooks/useIndirectTaxFilingAccess';
import IndirectTaxFilingCalendarNotice from '../../../indirect-tax/filings/components/IndirectTaxFilingCalendarNotice';
import IndirectTaxFilingCalendarRedirect from '../../../indirect-tax/filings/components/IndirectTaxFilingCalendarRedirect';
import { TCommonTaskProp } from '../../types';
import { ApproveFilingTask } from './ApproveFilingTask';
import { IndirectTaxFilingGenerateReportTask } from './IndirectTaxFilingGenerateReportTask';
import { IndirectTaxFilingSubmitTask } from './IndirectTaxFilingSubmitTask';

const IndirectTaxFilingWorkflowTask = (props: TCommonTaskProp) => {
  const { context, task } = props;
  const { serviceRequest } = context;
  const filingId =
    serviceRequest.salesTaxFiling?.filing_id ?? serviceRequest.salesTaxFilingId;
  const { filing, isLoading, error } = useIndirectTaxFilingV2(
    filingId,
    filingId ? { corporationId: serviceRequest.corporationId } : null
  );

  const region = IndirectTaxRegionHelper.deriveRegionFromJurisdiction({
    jurisdictionType: filing?.jurisdictionType,
    jurisdictionName: filing?.jurisdictionName,
    country:
      filing?.country ??
      serviceRequest.commendaService?.country ??
      serviceRequest.country,
  });
  const { canViewDetails } = useIndirectTaxFilingAccess(region);

  if (!canViewDetails)
    return (
      <IndirectTaxFilingCalendarRedirect
        corporationId={serviceRequest.corporationId}
      />
    );

  if (filingId) {
    if (isLoading) return <Skeleton className="h-12 w-full" />;
    if (error || !filing)
      return (
        <Alert
          variant="destructive"
          title="Filing unavailable"
          description="Unable to load filing details. Refresh to retry."
        />
      );
    if (!IndirectTaxRegionHelper.supportsFilingWorkflow(region))
      return <IndirectTaxFilingCalendarNotice />;
  }

  const stepDefinition =
    context.workflowExecution?.WorkflowTemplateVersion?.definition?.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    );
  const taskKey =
    stepDefinition?.legacyServiceRequestTaskKey ??
    task.legacyServiceRequestTaskTaskId;
  switch (taskKey) {
    case SalesTaxFilingTasks.GENERATE_FILING_REPORT:
      return <IndirectTaxFilingGenerateReportTask {...props} />;
    case SalesTaxFilingTasks.REVIEW_TRANSACTIONS:
      return <ApproveFilingTask {...props} />;
    case SalesTaxFilingTasks.CONFIRM_FILING:
      return <IndirectTaxFilingSubmitTask {...props} />;
    default:
      return null;
  }
};

export default IndirectTaxFilingWorkflowTask;
